import { listClasses as MuiListClasses, typographyClasses as MuiTypographyClasses, menuItemClasses, } from '@mui/material';
import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
import { randomStr } from '../../Shared';
const componentName = 'Dropdown';
const classPrefix = `ui-Dropdown`;
export const dropdownClasses = {
    // It is recommended that the root classname of the react-ui component be added with a random string.
    // This is mainly to avoid classname naming conflicts in the application
    root: `${classPrefix}-root-${randomStr()}`,
    subMenu: `${classPrefix}-subMenu`,
    menuItem: `${classPrefix}-menuItem`,
    menuItemDisabled: `${classPrefix}-menuItem-disabled`,
    menuItemSelected: `${classPrefix}-menuItem-selected`,
    menuItemDanger: `${classPrefix}-menuItem-danger`,
    menuItemExpend: `${classPrefix}-menu-expend`,
    menuItemText: `${classPrefix}-menuItem-text`,
    menuItemPrefix: `${classPrefix}-menuItem-prefix`,
    menuItemSuffix: `${classPrefix}-menuItem-suffix`,
    menuItemSuffixActive: `${classPrefix}-menuItem-suffixActive`,
    menuGroupTitle: `${classPrefix}-menuGroup-title`,
    menuGroupDivider: `${classPrefix}-menuGroup-divider`,
};
export function getMenuItemStyle(theme) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        boxSizing: 'border-box',
        padding: tk(token.padding.menuItem),
        fontSize: tk(token.fontSize),
        lineHeight: tk(token.lineHeight),
        color: tk(token.color.default),
        // backgroundColor: tk(token.background.default),
        backgroundColor: 'transparent',
        borderRadius: tk(token.borderRadius.menuItem),
        [`&.${dropdownClasses.menuItem}`]: {
            minHeight: '32px',
        },
        [`&.${menuItemClasses.focusVisible}`]: {
            backgroundColor: 'transparent',
        },
        [`&.${dropdownClasses.menuItem}:focus-visible`]: {
            color: tk(token.color.default),
            backgroundColor: 'transparent',
            outlineOffset: 2,
            outlineColor: tk(token.border.primary),
            outlineStyle: 'solid',
            outlineWidth: 2,
            zIndex: tk(token.zIndex),
            [`&.${dropdownClasses.menuItemDanger}`]: {
                color: tk(token.color.danger),
            },
        },
        [`&.${dropdownClasses.menuItem}:hover`]: {
            color: tk(token.color.default),
            backgroundColor: tk(token.background.hover),
        },
        [`&.${dropdownClasses.menuItem}:active`]: {
            backgroundColor: tk(token.background.press),
        },
        // disabled item
        [`&.${dropdownClasses.menuItemDisabled}`]: {
            backgroundColor: tk(token.background.disabled),
            color: tk(token.color.disabled),
            cursor: 'not-allowed',
            pointerEvents: 'inherit',
            opacity: 1,
            position: 'relative',
            '&::after': {
                position: 'absolute',
                content: '""',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                background: tk(token.disabledOverlay),
                borderRadius: tk(token.borderRadius.menuItem),
                pointerEvents: 'none',
            },
            [`&:hover`]: {
                backgroundColor: tk(token.background.disabled),
                color: tk(token.color.disabled),
                cursor: 'not-allowed',
            },
            [`&:active`]: {
                backgroundColor: tk(token.background.disabled),
                color: tk(token.color.disabled),
                cursor: 'not-allowed',
            },
        },
        // selected item
        [`&.${dropdownClasses.menuItemSelected}`]: {
            backgroundColor: 'transparent',
            color: tk(token.color.default),
            [`.${dropdownClasses.menuItemSuffixActive}`]: {
                color: tk(token.color.active),
            },
        },
        // danger item
        [`&.${dropdownClasses.menuItemDanger}`]: {
            backgroundColor: 'transparent',
            color: tk(token.color.danger),
            [`.${dropdownClasses.menuItemSuffixActive}`]: {
                color: tk(token.color.active),
            },
            '&:hover': {
                color: tk(token.color.danger),
            },
            '&:active': {
                color: tk(token.color.danger),
            },
        },
        // expend sub menu
        [`&.${dropdownClasses.menuItemExpend}`]: {
            backgroundColor: tk(token.background.active),
            color: tk(token.color.active),
            [`&:hover, &:active, &:focus-visible`]: {
                backgroundColor: tk(token.background.active),
                color: tk(token.color.active),
            },
            [`.${dropdownClasses.menuItemPrefix}, .${dropdownClasses.menuItemSuffix}`]: {
                color: 'inherit',
            },
        },
        [`.${dropdownClasses.menuItemPrefix}`]: {
            width: '16px',
            minWidth: '16px',
            textAlign: 'center',
            marginInlineEnd: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'inherit',
            '>*': {
                display: 'inline-flex',
            },
        },
        [`.${dropdownClasses.menuItemText}`]: {
            fontSize: tk(token.fontSize),
            lineHeight: '20px',
            [`.${MuiTypographyClasses.root}`]: {
                fontSize: tk(token.fontSize),
                lineHeight: '20px',
            },
        },
        [`.${dropdownClasses.menuItemSuffix}`]: {
            width: '16px',
            minWidth: '16px',
            textAlign: 'center',
            marginInlineStart: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            '>*': {
                display: 'inline-flex',
            },
        },
    };
    return style;
}
export function getMenuPopperStyle(theme) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        zIndex: tk(token.zIndex),
        [`.${MuiListClasses.root}`]: {
            padding: '8px',
        },
    };
    return style;
}
function getMenuGroupStyle(theme) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        [`.${dropdownClasses.menuGroupTitle}`]: {
            fontFamily: tk(token.fontFamily),
            height: 24,
            fontSize: 12,
            lineHeight: '16px',
            padding: '4px 8px',
            fontWeight: 400,
            boxSizing: 'border-box',
            color: tk(token.title.color),
            zIndex: tk(token.zIndex),
            borderRadius: tk(token.borderRadius.menuItem),
            [`&:focus-visible`]: {
                outlineOffset: 2,
                outlineColor: tk(token.border.primary),
                outlineStyle: 'solid',
                outlineWidth: 2,
                zIndex: tk(token.zIndex),
            },
        },
        [`.${dropdownClasses.menuGroupDivider}`]: {
            marginTop: '8px',
            marginBottom: '8px',
        },
    };
    return style;
}
export function getMenuWrapperStyle(theme, { singleMenu }) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        // fontSize: 30,
        backgroundColor: tk(token.paper.backgroundColor),
        border: `1px solid ${tk(token.paper.borderColor)}`,
        borderRadius: '8px',
        padding: '0',
        color: tk(token.paper.textColor),
        boxShadow: tk(token.paper.boxShadow),
        maxWidth: singleMenu ? 'calc(100vw - 16px)' : '50vw',
        [`.${MuiListClasses.root}`]: {
            padding: '8px',
        },
        ...getMenuGroupStyle(theme),
    };
    return style;
}
export function getSubMenuWrapperStyle(theme) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        backgroundColor: 'transparent',
        border: 'none',
        padding: '0 12px',
        boxShadow: 'none',
        zIndex: tk(token.zIndex),
        [`.${dropdownClasses.subMenu}`]: {
            margin: '-8px 0',
            maxWidth: '50vw',
            pointerEvents: 'auto',
            backgroundColor: tk(token.paper.backgroundColor),
            border: `1px solid ${tk(token.paper.borderColor)}`,
            borderRadius: '8px',
            padding: 0,
            color: tk(token.paper.textColor),
            boxShadow: tk(token.paper.boxShadow),
            [`.${MuiListClasses.root}`]: {
                padding: '8px',
            },
        },
        ...getMenuGroupStyle(theme),
    };
    return style;
}
export function getBackdropStyle(theme) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        zIndex: tk(token.zIndex),
        background: 'transparent',
    };
    return style;
}
